import { useEffect } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { useRouter } from './use-router';
import { getRouteAndState, deferDefaultRoute, getRouteComponent } from './logic';
import { Route, RouterProps } from './types';
import { logger } from '@bymason-private/instrumentation';

const LS_INTENDED_PATH = 'intendedPath';
const LS_LEGACY_TARGET_PATH = 'ltp';
const UNVERIFIED_ACCOUNT_LOG_MESSAGE =
  'Account email is not verified. Redirecting to unverified route or logging out';

/**
 * React component that encapuslates all routing for an application.
 */
export function RouteController({
  showLoading,
  loadingComponent,
  busyComponent,
  defaultRoute,
  loginRoute,
  logoutRoute,
  unvalidatedRoute,
  unAuthorizedRoute,
  routes,
  validatePermissions,
}: RouterProps): JSX.Element | null {
  const { currentURL, navigate } = useRouter();
  const { authError, authToken, isValidating, loginCallbackUrl, logout, userInfo } = useAuth();

  let navigateRoute: Route | null = null;
  let navigateState = {};

  //
  // this lifecycle hook runs AFTER each render
  // and it checks to see if any navigation was set
  // via the render logic.
  //
  // the navigation has to be set like this because
  // calling a direct navigate in the render logic
  // would cause a race with the in-progress render
  // cycle and result in a React warning in the
  // console along with possible side-effects.
  //
  useEffect(() => {
    if (navigateRoute) {
      navigate(navigateRoute, navigateState);
    }
  });

  //
  // start of render logic
  //

  if (isValidating) {
    // user auth is currently being validated, so return busy
    return busyComponent;
  }

  const [nextRoute, nextState] = getRouteAndState(currentURL.pathname, routes);

  /**
   * Unmatched Routes Logic
   */
  if (!nextRoute) {
    if (authToken && !userInfo?.email_verified) {
      //
      // user logged in but account is not validated
      //
      logger.warn(UNVERIFIED_ACCOUNT_LOG_MESSAGE);
      if (unvalidatedRoute) {
        return getRouteComponent(unvalidatedRoute, {});
      } else {
        logout && logout();
      }
    }

    //
    // route not recognized so check for login callback
    //
    const callbackURL = new URL(loginCallbackUrl);

    if (callbackURL.pathname === currentURL.pathname) {
      //
      // login callback detected, so check for intended route
      //
      const intendedPath = window.localStorage.getItem(LS_INTENDED_PATH);

      if (intendedPath) {
        const [intendedRoute, intendedState] = getRouteAndState(String(intendedPath), routes);

        if (intendedRoute) {
          navigateRoute = intendedRoute;
          navigateState = intendedState;

          return busyComponent;
        }
      }
    }

    //
    // route is still undetected, so defer navigation to default route
    //
    navigateRoute = deferDefaultRoute(defaultRoute);

    return busyComponent;
  }

  if (logout && nextRoute === logoutRoute) {
    logout();

    return busyComponent;
  }

  /**
   * Public Routes Logic
   */
  if (nextRoute?.public) {
    if (nextRoute === loginRoute) {
      //
      // route is login, which could've been a direct navigation from
      // user (e.g. via bookmark/link) or user could've redirected from
      // the legacy Controller application
      //

      if (authToken && userInfo?.email_verified && !authError) {
        //
        // user is logged in
        //

        // getting here *should* mean that user successfully logged-in
        // directly from the login page, so defer navigation to default route
        navigateRoute = deferDefaultRoute(defaultRoute);

        return busyComponent;
      }
    }

    // return corresponding component for route
    return getRouteComponent(nextRoute, nextState) || busyComponent;
  }

  /**
   * Private Routes Logic
   */
  if (!authToken || authError) {
    //
    // route is private and user is not authenticated, so store
    // the intended route and load the login route
    //
    window.localStorage.setItem(LS_INTENDED_PATH, window.location.href);
    return getRouteComponent(loginRoute, {}) || busyComponent;
  }

  if (!userInfo?.email_verified) {
    //
    // user logged in but account is not validated
    //
    logger.warn(UNVERIFIED_ACCOUNT_LOG_MESSAGE);
    if (unvalidatedRoute) {
      return getRouteComponent(unvalidatedRoute, {});
    } else {
      logout && logout();
    }
  }

  // reset cached intended paths
  window.localStorage.setItem(LS_INTENDED_PATH, '');
  window.localStorage.setItem(LS_LEGACY_TARGET_PATH, '');

  if (showLoading) {
    // show loading component for matching private routes when true
    return loadingComponent || busyComponent;
  }

  // route is private and user is authenticated, so check permissions if any specified
  if (validatePermissions && nextRoute.scope && !validatePermissions(nextRoute.scope)) {
    // user does not have permission to view route, so return unauthorized component if specified
    return (
      getRouteComponent(unAuthorizedRoute ?? defaultRoute, {
        scope: nextRoute.scope,
      }) || busyComponent
    );
  }

  // route is private but user is authenticated, so load it
  return getRouteComponent(nextRoute, nextState) || busyComponent;
}

/**
 * Computes and returns the href string for given view.
 */
export function getRouteUrl(routePath: string) {
  const currentUrl = new URL(window.location.href);
  const newPath = `${process.env.PUBLIC_URL}${routePath.startsWith('/') ? '' : '/'}${routePath}`;
  const routeUrl = new URL(`${currentUrl.origin}${newPath}`);

  return routeUrl.href;
}
